import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, BarChart3, Loader2, RefreshCw, Wrench } from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRUPO_LABELS = {
  receita_producao: 'Receita Produção', aporte_capital: 'Aporte Capital', venda_ativo: 'Venda Ativo',
  combustivel_maquinas: 'Combustível Máquinas', combustivel_veiculos: 'Combustível Veículos',
  lubrificacao: 'Lubrificação', insumos: 'Insumos', pecas: 'Peças', material_rodante: 'Material Rodante',
  manutencao_maquinas: 'Manutenção Máquinas', manutencao_veiculos: 'Manutenção Veículos',
  pessoal: 'Pessoal', deslocamento: 'Deslocamento', campo_operacao: 'Campo/Operação',
  saude: 'Saúde', administrativo: 'Administrativo', controle_interno: 'Controle Interno', residual: 'Residual',
};

function getMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return { data_inicio: `${y}-${m}-01`, data_fim: `${y}-${m}-${new Date(y, now.getMonth() + 1, 0).getDate()}` };
}

export default function FinanceiroDashboard({ userRole = 'admin', user = {} }) {
  const [loading, setLoading] = useState(true);
  const [saldo, setSaldo] = useState({ entradas: 0, saidas: 0, saldo_corrente: 0 });
  const [grupos, setGrupos] = useState([]);
  const [fluxo, setFluxo] = useState([]);
  const [periodo, setPeriodo] = useState(getMonthRange);
  const [showMaint, setShowMaint] = useState(false);
  const canAdmin = userRole === 'admin' || user?.is_master;

  const fetchData = useCallback(async () => {
    setLoading(true);
    const qs = `data_inicio=${periodo.data_inicio}&data_fim=${periodo.data_fim}`;
    try {
      const [sR, gR, fR] = await Promise.all([
        fetch(`${API_URL}/api/financeiro/saldo?${qs}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/financeiro/resumo/grupos?${qs}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/financeiro/relatorios/fluxo-de-caixa?${qs}&agrupamento=dia`, { headers: authHeaders() }),
      ]);
      if (sR.ok) setSaldo(await sR.json());
      if (gR.ok) setGrupos(await gR.json());
      if (fR.ok) { const d = await fR.json(); setFluxo(d.periodos || []); }
    } catch { toast.error('Erro ao carregar dashboard financeiro'); }
    finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runMaintenance = async (endpoint, label) => {
    try {
      toast.info(`Executando: ${label}...`);
      const r = await fetch(`${API_URL}${endpoint}`, { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (r.ok) {
        toast.success(d.message || 'Concluído');
        await fetchData();
      } else {
        toast.error(d.detail || 'Erro ao executar manutenção');
      }
    } catch {
      toast.error('Erro de conexão');
    }
  };

  const maxGrupo = Math.max(...grupos.map(g => g.total), 1);

  return (
    <div className="space-y-6" data-testid="financeiro-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Financeiro</h1>
          <p className="text-sm text-gray-500">Visão geral do período</p>
        </div>
        <div className="flex gap-2 items-center relative">
          <input type="date" value={periodo.data_inicio} onChange={e => setPeriodo(p => ({ ...p, data_inicio: e.target.value }))}
            className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-yellow-500 outline-none" data-testid="fin-date-start" />
          <input type="date" value={periodo.data_fim} onChange={e => setPeriodo(p => ({ ...p, data_fim: e.target.value }))}
            className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-yellow-500 outline-none" data-testid="fin-date-end" />
          <button
            onClick={fetchData}
            className="p-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-400 rounded-lg transition-colors"
            data-testid="fin-refresh-button"
            title="Recalcular dashboard (atualizar com dados atuais)"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          {canAdmin && (
            <>
              <button
                onClick={() => setShowMaint(s => !s)}
                className="p-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 rounded-lg transition-colors"
                data-testid="fin-maint-button"
                title="Manutenção admin (limpar duplicidades/órfãos)"
              >
                <Wrench size={18} />
              </button>
              {showMaint && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-[#111] border border-gray-700 rounded-lg shadow-xl z-[100] p-2">
                  <p className="text-xs text-gray-400 px-2 py-1.5">Manutenção do Financeiro</p>
                  <button onClick={() => runMaintenance('/api/admin/fuel/cleanup-abastecimento-lancamentos', 'Limpar lançamentos de abastecimento')}
                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-800 rounded" data-testid="maint-cleanup-abast">
                    🧹 Limpar lançamentos de abastecimento
                  </button>
                  <button onClick={() => runMaintenance('/api/admin/fuel/cleanup-orphan-compra-lancamentos', 'Limpar lançamentos órfãos')}
                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-800 rounded" data-testid="maint-cleanup-orphan">
                    🧹 Limpar lançamentos órfãos de compra
                  </button>
                  <button onClick={() => runMaintenance('/api/admin/fuel/backfill-compra-lancamentos', 'Backfill compras sem lançamento')}
                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-800 rounded" data-testid="maint-backfill">
                    🔁 Criar lançamentos faltantes de compra
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-yellow-500" size={32} /></div>
      ) : (
        <>
          {/* Cards Saldo */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#111] border border-gray-800 rounded-xl p-5" data-testid="card-entradas">
              <div className="flex items-center gap-2 mb-2"><ArrowUpRight size={18} className="text-emerald-400" /><span className="text-sm text-gray-400">Entradas</span></div>
              <p className="text-2xl font-bold text-emerald-400">R$ {saldo.entradas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-xl p-5" data-testid="card-saidas">
              <div className="flex items-center gap-2 mb-2"><ArrowDownRight size={18} className="text-red-400" /><span className="text-sm text-gray-400">Saídas</span></div>
              <p className="text-2xl font-bold text-red-400">R$ {saldo.saidas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-xl p-5" data-testid="card-saldo">
              <div className="flex items-center gap-2 mb-2"><DollarSign size={18} className="text-yellow-500" /><span className="text-sm text-gray-400">Saldo</span></div>
              <p className={`text-2xl font-bold ${saldo.saldo_corrente >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                R$ {saldo.saldo_corrente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {/* Fluxo de Caixa */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-yellow-500" />Fluxo de Caixa</h2>
            {fluxo.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">Nenhum lançamento no período</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex items-end gap-1 min-w-[600px] h-48">
                  {fluxo.map((d, i) => {
                    const maxVal = Math.max(...fluxo.map(f => Math.max(f.entradas, f.saidas)), 1);
                    const eH = (d.entradas / maxVal) * 100;
                    const sH = (d.saidas / maxVal) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                        <div className="flex gap-0.5 items-end h-36 w-full">
                          <div style={{ height: `${eH}%` }} className="flex-1 bg-emerald-500/60 rounded-t transition-all group-hover:bg-emerald-400/80" />
                          <div style={{ height: `${sH}%` }} className="flex-1 bg-red-500/60 rounded-t transition-all group-hover:bg-red-400/80" />
                        </div>
                        <span className="text-[9px] text-gray-600 truncate w-full text-center">{d.periodo.slice(5)}</span>
                        <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white whitespace-nowrap z-10">
                          <span className="text-emerald-400">+{d.entradas.toFixed(0)}</span> / <span className="text-red-400">-{d.saidas.toFixed(0)}</span> = <span className={d.saldo >= 0 ? 'text-emerald-400' : 'text-red-400'}>{d.saldo.toFixed(0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-2 justify-center">
                  <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-3 h-3 bg-emerald-500/60 rounded" />Entradas</span>
                  <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-3 h-3 bg-red-500/60 rounded" />Saídas</span>
                </div>
              </div>
            )}
          </div>

          {/* Resumo por Grupo */}
          <div className="bg-[#111] border border-gray-800 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-yellow-500" />Despesas por Grupo</h2>
            {grupos.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">Nenhuma despesa no período</p>
            ) : (
              <div className="space-y-3">
                {grupos.map((g, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-40 truncate">{GRUPO_LABELS[g.grupo] || g.grupo}</span>
                    <div className="flex-1 bg-gray-800 rounded-full h-5 overflow-hidden">
                      <div style={{ width: `${(g.total / maxGrupo) * 100}%` }}
                        className="h-full bg-gradient-to-r from-yellow-500/80 to-yellow-600/60 rounded-full transition-all duration-500" />
                    </div>
                    <span className="text-sm font-mono text-white w-28 text-right">R$ {g.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
